// Design template query/mutation hooks (templates.rs).
//   GET/POST /v1/workspaces/{ws}/templates, PATCH/DELETE /v1/templates/{id}
//
// PATCH and DELETE are scoped by templateId only on the wire, so callers
// pass workspaceId alongside for cache invalidation (same shape as locations.ts).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { templateKeys } from "./keys";
import type { Template, TemplateBody } from "./types";

export function useTemplates(workspaceId: string) {
    return useQuery({
        queryKey: templateKeys.list(workspaceId),
        queryFn: () => http.get<Template[]>(`/v1/workspaces/${workspaceId}/templates`),
        enabled: Boolean(workspaceId),
    });
}

export function useCreateTemplate(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TemplateBody) =>
            http.post<Template>(`/v1/workspaces/${workspaceId}/templates`, { json: body }),
        onSuccess: (created) => {
            // Seed the list cache so navigating straight into the new
            // template's editor finds it without a not-found flash while the
            // invalidated list refetches.
            queryClient.setQueryData<Template[]>(templateKeys.list(workspaceId), (list) =>
                list ? [...list, created] : [created],
            );
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}

export function usePatchTemplate(workspaceId: string, templateId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: TemplateBody) => http.patch<Template>(`/v1/templates/${templateId}`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}

export function useDeleteTemplate(workspaceId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        // The id arrives at mutate time so one hook instance serves a whole
        // list of rows.
        mutationFn: (templateId: string) => http.delete<void>(`/v1/templates/${templateId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: templateKeys.list(workspaceId) });
        },
    });
}
